import React, { useEffect, useState } from 'react';
import { Star, Pin, Eye, UserCheck, DollarSign, TrendingUp, Clock, Activity, Zap, ChevronRight, ArrowUp, ArrowDown, Play, Pause, Trash2, Calendar, CreditCard, Info, CheckCircle2, LayoutList, ExternalLink, Database, Server, History, Receipt, AlertCircle, RefreshCw, Mail, Settings, X, BarChart3, MousePointer, Sparkles, SplitSquareHorizontal, Edit, Target, Timer, ArrowRight, AlertTriangle, Layers } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface FeaturedAstrologersManagerProps {
  astrologers: any[];
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
  setShowCreatePin: (type: 'astrologer' | null) => void;
}
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(amount);
};
// Mock Audit Logs
const initialLogs = [{
  id: 'LOG-1',
  time: '10:42:15',
  type: 'auto_swap',
  message: 'Авто-замена: FA-01 завершен. Активирован FA-03.',
  status: 'success'
}, {
  id: 'LOG-2',
  time: '10:41:02',
  type: 'payment',
  message: 'Оплата подтверждена для FA-02. Статус изменен на "Активен".',
  status: 'success'
}, {
  id: 'LOG-3',
  time: '10:30:00',
  type: 'notification',
  message: 'Отправлено 24 SMS-уведомлений о старте кампаний.',
  status: 'info'
}];
export function FeaturedAstrologersManager({
  astrologers: initialAstrologers,
  setDetailPanel,
  success,
  setShowCreatePin
}: FeaturedAstrologersManagerProps) {
  const [campaigns, setCampaigns] = useState(initialAstrologers);
  const [activeModal, setActiveModal] = useState<'logs' | 'payment' | 'timing' | 'analytics' | 'ab_test' | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
  const [logs, setLogs] = useState(initialLogs);
  // Sync with props
  useEffect(() => {
    setCampaigns(initialAstrologers);
  }, [initialAstrologers]);
  const addLog = (type: string, message: string, status: string) => {
    setLogs(prev => [{
      id: `LOG-${Date.now()}`,
      time: new Date().toLocaleTimeString('ru-RU'),
      type,
      message,
      status
    }, ...prev].slice(0, 50));
  };
  const handleAction = (action: string, campaign: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    switch (action) {
      case 'view_profile':
        window.open(`/astrologer/${campaign.id}`, '_blank');
        break;
      case 'view_admin':
        setDetailPanel({
          type: 'featured-astrologer',
          title: campaign.astrologer,
          data: campaign
        });
        break;
      case 'view_advertiser':
        setDetailPanel({
          type: 'user-profile',
          title: campaign.advertiser,
          data: {
            name: campaign.advertiser,
            type: campaign.advertiserType
          }
        });
        break;
      case 'view_slot':
        setDetailPanel({
          type: 'slot-detail',
          title: campaign.slot,
          data: {
            name: campaign.slot
          }
        });
        break;
      case 'analytics':
        setSelectedCampaign(campaign);
        setActiveModal('analytics');
        break;
      case 'payment':
        setSelectedCampaign(campaign);
        setActiveModal('payment');
        break;
      case 'timing':
        setSelectedCampaign(campaign);
        setActiveModal('timing');
        break;
      case 'ab_test':
        setSelectedCampaign(campaign);
        setActiveModal('ab_test');
        break;
      case 'activate':
        if (campaign.paymentStatus !== 'paid') {
          addLog('error', `Отказ активации ${campaign.id}: Требуется оплата`, 'error');
          alert('Невозможно активировать: требуется оплата');
          return;
        }
        success(`Кампания ${campaign.id} активирована`);
        addLog('activation', `Кампания ${campaign.id} успешно активирована`, 'success');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'active'
        } : c));
        break;
      case 'pause':
        success(`Кампания ${campaign.id} приостановлена`);
        addLog('pause', `Кампания ${campaign.id} поставлена на паузу`, 'warning');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'paused'
        } : c));
        break;
      case 'fastTrack':
        success(`Fast Track включен для ${campaign.id}`);
        addLog('fast_track', `Fast Track включен для ${campaign.id}`, 'info');
        break;
      case 'delete':
        if (confirm(`Удалить кампанию ${campaign.id}?`)) {
          success(`Кампания ${campaign.id} удалена`);
          setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
        }
        break;
    }
  };
  // --- METRICS CALCULATION ---
  const totalImpressions = campaigns.reduce((acc, c) => acc + (c.impressions || 0), 0);
  const totalClicks = campaigns.reduce((acc, c) => acc + (c.clicks || 0), 0);
  const totalBookings = campaigns.reduce((acc, c) => acc + (c.bookings || 0), 0);
  const totalRevenue = campaigns.reduce((acc, c) => acc + (c.status === 'active' ? c.price : 0), 0);
  const avgConsultationPrice = 5000; // Mock avg check for consultation
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100).toFixed(2) : '0.00';
  const avgCr = totalClicks > 0 ? (totalBookings / totalClicks * 100).toFixed(2) : '0.00';
  const avgCpa = totalBookings > 0 ? Math.round(totalRevenue / totalBookings) : 0;
  const avgRpm = totalImpressions > 0 ? Math.round(totalRevenue / totalImpressions * 1000) : 0;
  const renderKpis = () => <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-id="element-12138">
      <Card padding="sm" hoverable className="bg-indigo-50 border-indigo-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12139">
        <div className="text-[10px] text-indigo-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12140">
          <Pin className="w-3 h-3" data-id="element-12141" /> Активные
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12142">
          {campaigns.filter(c => c.status === 'active').length}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-blue-50 border-blue-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12143">
        <div className="text-[10px] text-blue-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12144">
          <Eye className="w-3 h-3" data-id="element-12145" /> Показы
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12146">
          {totalImpressions.toLocaleString()}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-sky-50 border-sky-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12147">
        <div className="text-[10px] text-sky-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12148">
          <MousePointer className="w-3 h-3" data-id="element-12149" /> Клики
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12150">
          {totalClicks.toLocaleString()}
        </div>
        <div className="text-[10px] text-sky-600 mt-1" data-id="element-12151">CTR {avgCtr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-emerald-50 border-emerald-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12152">
        <div className="text-[10px] text-emerald-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12153">
          <UserCheck className="w-3 h-3" data-id="element-12154" /> Записи
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12155">
          {totalBookings.toLocaleString()}
        </div>
        <div className="text-[10px] text-emerald-600 mt-1" data-id="element-12156">CR {avgCr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-green-50 border-green-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12157">
        <div className="text-[10px] text-green-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12158">
          <DollarSign className="w-3 h-3" data-id="element-12159" /> Доход (Ads)
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12160">
          {formatCurrency(totalRevenue)}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-amber-50 border-amber-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12161">
        <div className="text-[10px] text-amber-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12162">
          <Target className="w-3 h-3" data-id="element-12163" /> CPA
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12164">
          {formatCurrency(avgCpa)}
        </div>
        <div className="text-[10px] text-amber-600 mt-1" data-id="element-12165">Цена за запись</div>
      </Card>
      <Card padding="sm" hoverable className="bg-purple-50 border-purple-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12166">
        <div className="text-[10px] text-purple-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12167">
          <Activity className="w-3 h-3" data-id="element-12168" /> RPM
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12169">
          {formatCurrency(avgRpm)}
        </div>
        <div className="text-[10px] text-purple-600 mt-1" data-id="element-12170">
          Доход на 1000 пок.
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-slate-50 border-slate-200 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-12171">
        <div className="text-[10px] text-slate-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-12172">
          <CreditCard className="w-3 h-3" data-id="element-12173" /> Ср. чек конс.
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-12174">
          {formatCurrency(avgConsultationPrice)}
        </div>
      </Card>
    </div>;
  const renderAiRecommendations = () => <Card padding="sm" className="mb-6 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50" data-id="element-12175">
      <div className="flex items-start gap-4" data-id="element-12176">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0" data-id="element-12177">
          <Sparkles className="w-5 h-5 text-indigo-600" data-id="element-12178" />
        </div>
        <div className="flex-1" data-id="element-12179">
          <h3 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-12180">
            AI Рекомендации для платформы
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-12181">
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-12182">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-12183">
                Оптимизация профиля
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-12184">
                Астролог FA-02 имеет низкий CR (3.4%). Рекомендуется обновить
                главное фото и добавить видео-визитку для повышения доверия.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-12185">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-12186">
                Повышение позиции
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-12187">
                Слот "Каталог астрологов - TOP 3" свободен со следующей недели.
                Предложите текущим рекламодателям апгрейд со скидкой 10%.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-12188">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-12189">
                A/B тест заголовков
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-12190">
                Запустите автоматическое A/B тестирование заголовков профилей в
                каталоге для увеличения общего CTR на 15-20%.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>;
  const renderTable = () => <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-12191">
      <div className="p-4 border-b border-slate-200 bg-white flex justify-between items-center" data-id="element-12192">
        <div className="flex items-center gap-3" data-id="element-12193">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-12194">
            <Star className="w-4 h-4 text-amber-500" data-id="element-12195" />
          </div>
          <div data-id="element-12196">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-12197">
              Управление продвижением астрологов
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-12198">
              Ads Manager для профилей специалистов
            </p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-12199">
          <Button variant="outline" size="sm" icon={<History className="w-4 h-4" data-id="element-12201" />} onClick={() => setActiveModal('logs')} data-id="element-12200">
            Audit Logs
          </Button>
          <Button variant="primary" size="sm" icon={<Pin className="w-4 h-4" data-id="element-12203" />} onClick={() => setShowCreatePin('astrologer')} data-id="element-12202">
            Создать кампанию
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-12204">
        <table className="w-full text-left whitespace-nowrap" data-id="element-12205">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-12206">
            <tr data-id="element-12207">
              <th className="px-4 py-3 font-medium" data-id="element-12208">Кампания / Астролог</th>
              <th className="px-4 py-3 font-medium" data-id="element-12209">Рекламодатель</th>
              <th className="px-4 py-3 font-medium" data-id="element-12210">Слот / Позиция</th>
              <th className="px-4 py-3 font-medium" data-id="element-12211">Таймер / Период</th>
              <th className="px-4 py-3 font-medium" data-id="element-12212">Коммерция</th>
              <th className="px-4 py-3 font-medium" data-id="element-12213">
                Воронка (Показы → Клики → Записи)
              </th>
              <th className="px-4 py-3 font-medium" data-id="element-12214">Статус / Очередь</th>
              <th className="px-4 py-3 text-right font-medium" data-id="element-12215">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-12216">
            {campaigns.map(c => {
            const ctr = c.impressions > 0 ? (c.clicks / c.impressions * 100).toFixed(1) : '0.0';
            const cr = c.clicks > 0 ? (c.bookings / c.clicks * 100).toFixed(1) : '0.0';
            const isPaid = c.paymentStatus === 'paid';
            // Timer logic simulation
            let timerColor = 'text-slate-500';
            let timerText = 'Ожидание';
            if (c.status === 'active') {
              timerColor = 'text-emerald-600';
              timerText = 'Осталось 14 дн.';
            } else if (c.status === 'queued') {
              timerColor = 'text-amber-600';
              timerText = 'Старт через 4 дн.';
            }
            return <tr key={c.id} className="hover:bg-slate-50 transition-colors group bg-white" data-id="element-12217">
                  {/* Astrologer Info */}
                  <td className="px-4 py-3" data-id="element-12218">
                    <div className="flex items-center gap-2 mb-1" data-id="element-12219">
                      <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:underline" onClick={() => handleAction('view_admin', c)} data-id="element-12220">
                        {c.id}
                      </span>
                      {c.creatives?.length > 1 && <Badge variant="pro" size="sm" className="text-[9px] py-0 cursor-pointer" onClick={() => handleAction('ab_test', c)} data-id="element-12221">
                          <SplitSquareHorizontal className="w-2.5 h-2.5 mr-0.5" data-id="element-12222" />{' '}
                          A/B Test
                        </Badge>}
                    </div>
                    <div className="text-sm font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1" onClick={() => handleAction('view_profile', c)} data-id="element-12223">
                      {c.astrologer}{' '}
                      <ExternalLink className="w-3 h-3 opacity-50" data-id="element-12224" />
                    </div>
                  </td>

                  {/* Advertiser */}
                  <td className="px-4 py-3" data-id="element-12225">
                    <div className="text-xs font-medium text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5" onClick={() => handleAction('view_advertiser', c)} data-id="element-12226">
                      <UserCheck className="w-3.5 h-3.5 text-slate-400" data-id="element-12227" />
                      {c.advertiser}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-12228">
                      {c.advertiserType === 'astrologer' ? 'Сам астролог' : 'Продюсер / Агентство'}
                    </div>
                  </td>

                  {/* Slot & Position */}
                  <td className="px-4 py-3" data-id="element-12229">
                    <div className="text-xs font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5 mb-1" onClick={() => handleAction('view_slot', c)} data-id="element-12230">
                      <LayoutList className="w-3.5 h-3.5 text-slate-400" data-id="element-12231" />
                      {c.slot}
                    </div>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-12232">
                      {c.position}
                    </Badge>
                  </td>

                  {/* Timer & Period */}
                  <td className="px-4 py-3" data-id="element-12233">
                    <div className={`text-xs font-bold flex items-center gap-1.5 mb-1 cursor-pointer hover:underline ${timerColor}`} onClick={() => handleAction('timing', c)} data-id="element-12234">
                      <Clock className="w-3.5 h-3.5" data-id="element-12235" />
                      {timerText}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-1" data-id="element-12236">
                      <Calendar className="w-3 h-3" data-id="element-12237" />
                      {c.periodStart} — {c.periodEnd}
                    </div>
                  </td>

                  {/* Commerce */}
                  <td className="px-4 py-3" data-id="element-12238">
                    <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-12239">
                      {formatCurrency(c.price)}
                    </div>
                    <div className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded cursor-pointer transition-colors ${isPaid ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`} onClick={() => handleAction('payment', c)} data-id="element-12240">
                      <Receipt className="w-2.5 h-2.5" data-id="element-12241" />
                      {isPaid ? 'Оплачено' : 'Ожидает оплаты'}
                    </div>
                  </td>

                  {/* Funnel Metrics */}
                  <td className="px-4 py-3" data-id="element-12242">
                    <div className="flex items-center gap-4 cursor-pointer hover:bg-slate-50 p-1 rounded transition-colors" onClick={() => handleAction('analytics', c)} data-id="element-12243">
                      <div className="flex flex-col items-center w-16" data-id="element-12244">
                        <span className="text-xs font-bold text-slate-900" data-id="element-12245">
                          {c.impressions.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-12246">
                          Показы
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-12247" />
                      <div className="flex flex-col items-center w-16" data-id="element-12248">
                        <span className="text-xs font-bold text-blue-600" data-id="element-12249">
                          {c.clicks.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-12250">
                          CTR {ctr}%
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-12251" />
                      <div className="flex flex-col items-center w-16" data-id="element-12252">
                        <span className="text-xs font-bold text-emerald-600" data-id="element-12253">
                          {c.bookings.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-12254">
                          CR {cr}%
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Status & Queue */}
                  <td className="px-4 py-3" data-id="element-12255">
                    <div className="flex flex-col gap-1.5 items-start" data-id="element-12256">
                      <Badge variant={c.status === 'active' ? 'success' : c.status === 'queued' ? 'warning' : 'neutral'} size="sm" data-id="element-12257">
                        {c.status === 'active' ? 'Транслируется' : c.status === 'queued' ? 'В очереди' : 'Пауза'}
                      </Badge>

                      {c.queuePosition && <div className="text-[10px] text-indigo-600 font-medium bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:bg-indigo-100 flex items-center gap-1" onClick={() => handleAction('timing', c)} data-id="element-12258">
                          <LayoutList className="w-3 h-3" data-id="element-12259" />
                          Очередь: #{c.queuePosition}
                        </div>}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right" data-id="element-12260">
                    <div className="flex items-center justify-end gap-1" data-id="element-12261">
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Аналитика" onClick={() => handleAction('analytics', c)} data-id="element-12262">
                        <BarChart3 className="w-3.5 h-3.5" data-id="element-12263" />
                      </Button>
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Редактировать" onClick={() => handleAction('view_admin', c)} data-id="element-12264">
                        <Edit className="w-3.5 h-3.5" data-id="element-12265" />
                      </Button>
                      {c.status === 'active' ? <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-amber-500 hover:bg-amber-50" title="Пауза" onClick={() => handleAction('pause', c)} data-id="element-12266">
                          <Pause className="w-3.5 h-3.5" data-id="element-12267" />
                        </Button> : <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-emerald-500 hover:bg-emerald-50" title="Активировать" onClick={e => handleAction('activate', c, e)} data-id="element-12268">
                          <Play className="w-3.5 h-3.5 fill-current" data-id="element-12269" />
                        </Button>}
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-rose-400 hover:text-rose-600 hover:bg-rose-50" title="Удалить" onClick={() => handleAction('delete', c)} data-id="element-12270">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-12271" />
                      </Button>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
  const renderModals = () => {
    if (!activeModal) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setActiveModal(null);
    }} data-id="element-12272">
        {/* LOGS MODAL */}
        {activeModal === 'logs' && <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12273">
            <div className="p-4 border-b border-slate-200 bg-slate-900 text-white flex justify-between items-center shrink-0" data-id="element-12274">
              <div className="flex items-center gap-3" data-id="element-12275">
                <Server className="w-5 h-5 text-indigo-400" data-id="element-12276" />
                <div data-id="element-12277">
                  <h3 className="font-bold" data-id="element-12278">Audit Logs: Ads Manager</h3>
                  <p className="text-[10px] text-slate-400" data-id="element-12279">
                    Журнал событий рекламной платформы
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" icon={<X className="w-5 h-5" data-id="element-12281" />} onClick={() => setActiveModal(null)} data-id="element-12280" />
            </div>
            <div className="p-0 overflow-y-auto bg-slate-950 flex-1 font-mono text-xs" data-id="element-12282">
              {logs.map(log => <div key={log.id} className="flex gap-4 p-3 border-b border-slate-800 hover:bg-slate-900/50" data-id="element-12283">
                  <div className="text-slate-500 shrink-0" data-id="element-12284">{log.time}</div>
                  <div className={`shrink-0 w-24 ${log.status === 'error' ? 'text-rose-400' : log.status === 'warning' ? 'text-amber-400' : log.status === 'success' ? 'text-emerald-400' : 'text-blue-400'}`} data-id="element-12285">
                    [{log.type.toUpperCase()}]
                  </div>
                  <div className="text-slate-300" data-id="element-12286">{log.message}</div>
                </div>)}
            </div>
          </Card>}

        {/* PAYMENT MODAL */}
        {activeModal === 'payment' && selectedCampaign && <Card className="w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12287">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-12288">
              <div className="flex items-center gap-2" data-id="element-12289">
                <Receipt className="w-5 h-5 text-indigo-600" data-id="element-12290" />
                <h3 className="font-bold text-slate-900" data-id="element-12291">
                  Детали оплаты кампании
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-12293" />} onClick={() => setActiveModal(null)} data-id="element-12292" />
            </div>
            <div className="p-6 space-y-6" data-id="element-12294">
              <div className="text-center" data-id="element-12295">
                <div className="text-sm text-slate-500 mb-1" data-id="element-12296">
                  Сумма к оплате
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-12297">
                  {formatCurrency(selectedCampaign.price || 0)}
                </div>
                <Badge variant={selectedCampaign.paymentStatus === 'paid' ? 'success' : 'warning'} className="mt-2" data-id="element-12298">
                  {selectedCampaign.paymentStatus === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                </Badge>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm" data-id="element-12299">
                <div className="flex justify-between" data-id="element-12300">
                  <span className="text-slate-500" data-id="element-12301">ID Кампании</span>
                  <span className="font-bold text-slate-900" data-id="element-12302">
                    {selectedCampaign.id}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-12303">
                  <span className="text-slate-500" data-id="element-12304">Инвойс</span>
                  <span className="font-mono text-indigo-600" data-id="element-12305">
                    {selectedCampaign.invoiceId || 'INV-PENDING'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-12306">
                  <span className="text-slate-500" data-id="element-12307">Транзакция</span>
                  <span className="font-mono text-slate-900" data-id="element-12308">
                    {selectedCampaign.paymentId || '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-12309">
                  <span className="text-slate-500" data-id="element-12310">Рекламодатель</span>
                  <span className="font-medium text-slate-900" data-id="element-12311">
                    {selectedCampaign.advertiser}
                  </span>
                </div>
              </div>

              {selectedCampaign.paymentStatus !== 'paid' && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-id="element-12312">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" data-id="element-12313" />
                  <div className="text-sm text-amber-800" data-id="element-12314">
                    <strong data-id="element-12315">Блокировка активации:</strong> Кампания не будет
                    запущена до подтверждения оплаты.
                  </div>
                </div>}

              <div className="flex gap-3 pt-4 border-t border-slate-100" data-id="element-12316">
                <Button variant="outline" className="flex-1" onClick={() => setActiveModal(null)} data-id="element-12317">
                  Закрыть
                </Button>
                {selectedCampaign.paymentStatus !== 'paid' && <Button variant="primary" className="flex-1" onClick={() => {
              success('Оплата подтверждена');
              addLog('payment', `Ручное подтверждение оплаты для ${selectedCampaign.id}`, 'success');
              setCampaigns(prev => prev.map(c => c.id === selectedCampaign.id ? {
                ...c,
                paymentStatus: 'paid',
                paymentId: 'PAY-MANUAL-' + Date.now()
              } : c));
              setActiveModal(null);
            }} data-id="element-12318">
                    Подтвердить оплату
                  </Button>}
              </div>
            </div>
          </Card>}

        {/* TIMING MODAL */}
        {activeModal === 'timing' && selectedCampaign && <Card className="w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12319">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-12320">
              <div className="flex items-center gap-2" data-id="element-12321">
                <Clock className="w-5 h-5 text-indigo-600" data-id="element-12322" />
                <h3 className="font-bold text-slate-900" data-id="element-12323">Тайминг и Очередь</h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-12325" />} onClick={() => setActiveModal(null)} data-id="element-12324" />
            </div>
            <div className="p-6 space-y-4 text-sm" data-id="element-12326">
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12327">
                <span className="text-slate-500" data-id="element-12328">Статус</span>
                <Badge variant={selectedCampaign.status === 'active' ? 'success' : 'warning'} data-id="element-12329">
                  {selectedCampaign.status === 'active' ? 'Транслируется' : 'В очереди'}
                </Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12330">
                <span className="text-slate-500" data-id="element-12331">Период</span>
                <span className="font-medium text-slate-900" data-id="element-12332">
                  {selectedCampaign.periodStart} — {selectedCampaign.periodEnd}
                </span>
              </div>

              {selectedCampaign.status === 'queued' && <>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12333">
                    <span className="text-slate-500" data-id="element-12334">Позиция в очереди</span>
                    <span className="font-bold text-indigo-600" data-id="element-12335">
                      #{selectedCampaign.queuePosition}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12336">
                    <span className="text-slate-500" data-id="element-12337">Ожидаемый старт</span>
                    <span className="font-bold text-slate-900" data-id="element-12338">
                      {selectedCampaign.expectedStartAt || 'Неизвестно'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-12339">
                    <span className="text-slate-500" data-id="element-12340">Время ожидания</span>
                    <span className="font-medium text-amber-600" data-id="element-12341">
                      {selectedCampaign.waitTime || '—'}
                    </span>
                  </div>
                </>}

              <div className="mt-6 bg-indigo-50 p-4 rounded-xl text-xs text-indigo-800 flex gap-2" data-id="element-12342">
                <Info className="w-4 h-4 shrink-0 mt-0.5" data-id="element-12343" />
                <p data-id="element-12344">
                  Система автоматически активирует кампанию при освобождении
                  слота "{selectedCampaign.slot}" (onExpire event).
                </p>
              </div>
            </div>
          </Card>}

        {/* ANALYTICS MODAL */}
        {activeModal === 'analytics' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12345">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-12346">
              <div className="flex items-center gap-2" data-id="element-12347">
                <BarChart3 className="w-5 h-5 text-indigo-600" data-id="element-12348" />
                <h3 className="font-bold text-slate-900" data-id="element-12349">
                  Аналитика кампании: {selectedCampaign.astrologer}
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-12351" />} onClick={() => setActiveModal(null)} data-id="element-12350" />
            </div>
            <div className="p-6" data-id="element-12352">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-12353">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-12354">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12355">Показы</div>
                  <div className="text-2xl font-bold text-slate-900" data-id="element-12356">
                    {selectedCampaign.impressions.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-12357">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12358">Клики</div>
                  <div className="text-2xl font-bold text-blue-600" data-id="element-12359">
                    {selectedCampaign.clicks.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-12360">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12361">CTR</div>
                  <div className="text-2xl font-bold text-indigo-600" data-id="element-12362">
                    {selectedCampaign.impressions > 0 ? (selectedCampaign.clicks / selectedCampaign.impressions * 100).toFixed(2) : '0.00'}
                    %
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-12363">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-12364">Записи</div>
                  <div className="text-2xl font-bold text-emerald-600" data-id="element-12365">
                    {selectedCampaign.bookings.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="h-48 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center mb-6" data-id="element-12366">
                <div className="text-slate-400 flex items-center gap-2" data-id="element-12367">
                  <BarChart3 className="w-5 h-5" data-id="element-12368" />
                  <span data-id="element-12369">График конверсий (Mock)</span>
                </div>
              </div>

              <div className="flex justify-end" data-id="element-12370">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-12371">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}

        {/* A/B TEST MODAL */}
        {activeModal === 'ab_test' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-12372">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-12373">
              <div className="flex items-center gap-2" data-id="element-12374">
                <SplitSquareHorizontal className="w-5 h-5 text-indigo-600" data-id="element-12375" />
                <h3 className="font-bold text-slate-900" data-id="element-12376">
                  A/B Тестирование профиля
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-12378" />} onClick={() => setActiveModal(null)} data-id="element-12377" />
            </div>
            <div className="p-6" data-id="element-12379">
              <p className="text-sm text-slate-600 mb-6" data-id="element-12380">
                Сравнение эффективности различных вариантов карточки для
                кампании <strong data-id="element-12381">{selectedCampaign.id}</strong>.
              </p>

              <div className="space-y-4" data-id="element-12382">
                {selectedCampaign.creatives?.map((cr: any, idx: number) => <div key={cr.id} className={`p-4 rounded-xl border ${idx === 0 ? 'border-emerald-400 bg-emerald-50/30' : 'border-slate-200 bg-white'} flex items-center justify-between`} data-id="element-12383">
                    <div className="flex items-center gap-4" data-id="element-12384">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${idx === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-12385">
                        {idx === 0 ? 'A' : 'B'}
                      </div>
                      <div data-id="element-12386">
                        <div className="font-bold text-slate-900" data-id="element-12387">
                          {cr.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-12388">
                          ID: {cr.id}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-6 text-center" data-id="element-12389">
                      <div data-id="element-12390">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-12391">
                          CTR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-12392">
                          {cr.ctr}
                        </div>
                      </div>
                      <div data-id="element-12393">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-12394">
                          CR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-12395">
                          {cr.cr}
                        </div>
                      </div>
                    </div>
                    {idx === 0 && <Badge variant="success" data-id="element-12396">Winner</Badge>}
                  </div>)}
              </div>

              <div className="mt-6 flex justify-end" data-id="element-12397">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-12398">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}
      </div>;
  };
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-12399">
      {renderKpis()}
      {renderAiRecommendations()}
      {renderTable()}
      {renderModals()}
    </div>;
}